<template>
  <Tippy
    v-if="useRichTextTooltip"
    tag="span"
    class="dictionary-info-icon"
    :placement="tooltipPlacement"
    :max-width="tooltipMaxWidth"
    trigger="mouseenter focus"
    interactive
  >
    <InfoIcon v-bind="infoIconProps" tooltip-text="" />
    <template #content>
      <DictionaryRichText
        :dictionary-key="dictionaryKey"
        :fallback="fallback"
      />
    </template>
  </Tippy>
  <InfoIcon v-else v-bind="infoIconProps" :tooltip-text="htmlTooltipText" />
</template>

<script setup lang="ts">
import { InfoIcon } from '@hypercodestudio/basler-components';
import type { InfoIconProps } from '@hypercodestudio/basler-components';

import { Tippy } from 'vue-tippy';
import DictionaryRichText from '~/components/global/DictionaryRichText.vue';
import { useHTMLDictionary } from '~/composables/useHTMLDictionary';
import { useRichTextDictionary } from '~/composables/useRichTextDictionary';
import { richTextNeedsComponentRenderer } from '~/utils/richtext/richTextNeedsComponentRenderer';

interface Props {
  dictionaryKey: string;
  fallback?: string;
  tag?: 'button' | 'span';
  hasPulseAnimation?: boolean;
  tooltipPlacement?:
    | 'auto'
    | 'auto-start'
    | 'auto-end'
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'left-start';
  tooltipMaxWidth?: number;
}

const props = withDefaults(defineProps<Props>(), {
  fallback: undefined,
  tag: 'button',
  hasPulseAnimation: false,
  tooltipPlacement: 'auto',
  tooltipMaxWidth: 350
});

defineOptions({
  inheritAttrs: false
});

const attrs = useAttrs();
const { $textDictionary } = useNuxtApp();

const dictionaryKeys = computed(() => [props.dictionaryKey]);

const richTextDictionary = useRichTextDictionary(dictionaryKeys);
const htmlDictionary = useHTMLDictionary(dictionaryKeys);

const richText = computed(() => richTextDictionary.value[props.dictionaryKey]);

/** Embedded entries (images, etc.) need LibRichTextRenderer — see DBP-2863. */
const useRichTextTooltip = computed(() => {
  const document = richText.value;
  return !!document && richTextNeedsComponentRenderer(document);
});

const plainTooltipText = computed(
  () => $textDictionary.value?.[props.dictionaryKey] ?? props.fallback ?? ''
);

/** HTML-string tooltips — same pattern as SalesforceTicketCreateView / PDP shipping. */
const htmlTooltipText = computed(() => {
  const html = htmlDictionary.value[props.dictionaryKey];
  if (html) {
    return `<div class="rte-text">${html}</div>`;
  }

  return plainTooltipText.value;
});

const infoIconProps = computed<InfoIconProps>(() => {
  const { style, ...restAttrs } = attrs as { style?: InfoIconProps['style'] };

  return {
    ...restAttrs,
    style,
    tag: props.tag,
    hasPulseAnimation: props.hasPulseAnimation,
    tooltipPlacement: props.tooltipPlacement,
    tooltipMaxWidth: props.tooltipMaxWidth
  };
});
</script>

<style scoped>
.dictionary-info-icon {
  display: inline-flex;
  vertical-align: middle;
}
</style>
